import DataTableBase from "./DataTableBase";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo } from "react";
import fetchData from "../../API/Fetch";
import { Box, Typography } from "@mui/material";
import { Schedule, Sensors } from "@mui/icons-material";

// Fonction pour formater une date en format français (JJ/MM/AA HH:mm)
const formatDateFR = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2); // 2 derniers chiffres
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return dateString;
  }
};

// Colonnes de date/heure à formater
const dateColumns = ["time", "time_created", "created_at", "updated_at", "timestamp", "date", "datetime"];

export default function DeviceHistoryTable(props) {
  const dev_eui = props.dev_eui;

  const { t } = useTranslation("common");

  const [deviceHistory, setDeviceHistory] = React.useState([]);
  const [rawColumns, setRawColumns] = React.useState([]);

  useEffect(() => {
    if (!dev_eui) return;

    const fetchHistory = async () => {
      // Essayer d'abord l'endpoint avec les 24 dernières heures
      let dhFetch = await fetchData("/deviceHistory/" + dev_eui + "/last24h");

      // Si pas de données ou erreur, fallback sur l'ancien endpoint
      if (!dhFetch || dhFetch.length === 0) {
        dhFetch = await fetchData("/deviceHistory/" + dev_eui + "/last");
      }

      setDeviceHistory(dhFetch || []);

      // Récupérer les clés de la première entrée comme colonnes
      if (dhFetch && dhFetch.length > 0) {
        setRawColumns(Object.keys(dhFetch[0]));
      }
    };

    fetchHistory();
  }, [dev_eui]);

  // Construire les colonnes avec formatage personnalisé
  const columns = useMemo(() => {
    if (!rawColumns || rawColumns.length === 0) return [];

    return rawColumns.map((col) => {
      const isDateColumn = dateColumns.some(dc => col.toLowerCase().includes(dc.toLowerCase()));

      return {
        name: col,
        label: col.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
        options: {
          filter: true,
          sort: true,
          customBodyRender: (value) => {
            // Formatage des colonnes de date
            if (isDateColumn) {
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Schedule sx={{ color: "#6b9dad", fontSize: 18 }} />
                  <Typography sx={{ color: "#ffffff", fontSize: "0.875rem" }}>
                    {formatDateFR(value)}
                  </Typography>
                </Box>
              );
            }

            // Autres valeurs
            return (
              <Typography sx={{ color: "#ffffff", fontSize: "0.875rem" }}>
                {value !== null && value !== undefined ? String(value) : "-"}
              </Typography>
            );
          },
        },
      };
    });
  }, [rawColumns]);

  return (
    <DataTableBase
      columns={columns}
      data={deviceHistory}
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Sensors sx={{ color: "#c4a574" }} />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {t("common.deviceHistory")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", ml: 1 }}>
            ({deviceHistory.length} {deviceHistory.length > 1 ? "entrées" : "entrée"})
          </Typography>
        </Box>
      }
      id="devicehistory-list"
      name="devicehistory-list"
      pageSize={10}
      sortOrder="time_created"
      options={{
        sortOrder: {
          name: "time_created",
          direction: "desc",
        },
      }}
    />
  );
}
